import { useRef, useState, useEffect } from "react";

export interface VideoPlayerProps {
  src: string;
  poster?: string;
  title?: string;
  autoPlay?: boolean;
  autoPlayOnMute?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  className?: string;
  onEnded?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  captionsUrl?: string;
}

export const VideoPlayer = ({
  src,
  poster,
  title,
  autoPlay = false,
  autoPlayOnMute = false,
  muted: initialMuted = false,
  loop = false,
  controls = true,
  className = "",
  onEnded,
  onPlay,
  onPause,
  captionsUrl,
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isControlsVisible, setIsControlsVisible] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isMuted, setIsMuted] = useState(autoPlayOnMute || initialMuted);
  const [shouldShowTitle, setShouldShowTitle] = useState(true);
  const [isTitleVisible, setIsTitleVisible] = useState(true);
  const [isInteractingWithControls, setIsInteractingWithControls] =
    useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const titleTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const shouldAutoPlay = autoPlay || (autoPlayOnMute && isMuted);
  const isInitialAutoplay = useRef(shouldAutoPlay);
  const lastInteractionRef = useRef<number>(Date.now());
  const [hasCaptions, setHasCaptions] = useState(false);
  const [showCaptions, setShowCaptions] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      const calculatedProgress = (video.currentTime / video.duration) * 100;
      setProgress(isNaN(calculatedProgress) ? 0 : calculatedProgress);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      // Initialize progress to 0 when video loads
      setProgress(0);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      if (!autoPlayOnMute) {
        setIsTitleVisible(false);
        titleTimeoutRef.current = setTimeout(() => {
          setShouldShowTitle(false);
        }, 300);
      }
      if (!isInitialAutoplay.current) {
        onPlay?.();
      }
      isInitialAutoplay.current = false;
    };

    const handlePause = () => {
      setIsPlaying(false);
      onPause?.();
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      if (titleTimeoutRef.current) {
        clearTimeout(titleTimeoutRef.current);
      }
    };
  }, [videoRef.current]); // Only re-run if video element changes

  useEffect(() => {
    // Ensure video is muted when autoPlayOnMute is true
    if (videoRef.current && autoPlayOnMute) {
      videoRef.current.muted = true;
      setIsMuted(true);
    }
  }, [autoPlayOnMute]);

  const handleUnmute = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;

    setHasInteracted(true);
    setIsTitleVisible(false);
    titleTimeoutRef.current = setTimeout(() => {
      setShouldShowTitle(false);
    }, 300);
    setIsMuted(false);
    videoRef.current.muted = false;
    videoRef.current.currentTime = 0;
    try {
      await videoRef.current.play();
    } catch (error) {
      console.error("Failed to play video:", error);
    }
  };

  const togglePlay = (e?: React.MouseEvent) => {
    if (
      e?.target instanceof Element &&
      (e.target.closest(".video-controls") || e.target.closest(".play-button"))
    ) {
      return;
    }

    if (autoPlayOnMute && isMuted && !hasInteracted) {
      handleUnmute(e as React.MouseEvent);
      return;
    }

    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;

    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const newTime = (percentage / 100) * duration;

    videoRef.current.currentTime = newTime;
    setProgress(percentage);
  };

  // Unified control visibility handler
  const handleUserActivity = () => {
    lastInteractionRef.current = Date.now();
    setIsControlsVisible(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  };

  // Auto-hide controls after inactivity
  useEffect(() => {
    if (!isPlaying || isInteractingWithControls) return;

    const checkInactivity = () => {
      const now = Date.now();
      const inactiveTime = now - lastInteractionRef.current;

      if (inactiveTime >= 3000 && !isInteractingWithControls) {
        // 3 seconds
        setIsControlsVisible(false);
      }
    };

    const inactivityTimer = setInterval(checkInactivity, 1000);
    return () => clearInterval(inactivityTimer);
  }, [isPlaying, isInteractingWithControls]);

  // Clear any existing timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  const handleControlsInteractionStart = () => {
    setIsInteractingWithControls(true);
    handleUserActivity();
  };

  const handleControlsInteractionEnd = () => {
    setIsInteractingWithControls(false);
    handleUserActivity();
  };

  // Add track for captions when available
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTrackLoad = () => {
      const hasAvailableTracks = video.textTracks.length > 0;
      setHasCaptions(hasAvailableTracks);

      if (hasAvailableTracks) {
        const track = video.textTracks[0];
        // Initialize track mode based on showCaptions state
        track.mode = showCaptions ? "showing" : "hidden";
      }
    };

    video.addEventListener("loadedmetadata", handleTrackLoad);
    return () => {
      video.removeEventListener("loadedmetadata", handleTrackLoad);
    };
  }, [videoRef.current, showCaptions]);

  // Update track mode when showCaptions changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video || video.textTracks.length === 0) return;

    const track = video.textTracks[0];
    track.mode = showCaptions ? "showing" : "hidden";
  }, [showCaptions]);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    setIsMuted(!isMuted);
    videoRef.current.muted = !isMuted;
  };

  const toggleCaptions = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    setShowCaptions(!showCaptions);
    const track = videoRef.current.textTracks[0];
    if (track) {
      track.mode = showCaptions ? "hidden" : "showing";
    }
  };

  return (
    <div
      className={`relative aspect-video bg-brand-50 overflow-hidden ${className}`}
      onMouseMove={handleUserActivity}
      onTouchStart={handleUserActivity}
      onTouchEnd={handleUserActivity}
      onClick={togglePlay}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        src={src}
        poster={poster}
        autoPlay={shouldAutoPlay}
        muted={isMuted}
        loop={loop}
        playsInline
        onEnded={onEnded}
      >
        {captionsUrl && (
          <track
            kind="subtitles"
            src={captionsUrl}
            srcLang="en"
            label="English"
            default
          />
        )}
      </video>

      {controls && (
        <>
          {/* Title - show based on shouldShowTitle state with fade animation */}
          {title && shouldShowTitle && (
            <div
              className={`absolute top-0 left-0 right-0 px-6 pb-12 pt-6 bg-gradient-to-b from-brand-800/70 via-brand-800/30 to-transparent transition-opacity duration-300 ${
                isTitleVisible ? "opacity-100" : "opacity-0"
              }`}
            >
              <h3 className="text-brand-50 text-lg font-medium">{title}</h3>
            </div>
          )}

          {/* Unmute Button - show only when video is muted and hasn't been interacted with */}
          {isMuted && !hasInteracted && autoPlayOnMute && (
            <button
              className="unmute-button absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hover:scale-110 transition-transform pointer-events-none"
              aria-label="Click anywhere to unmute"
              data-testid="video-player-unmute-button"
            >
              <div className="flex flex-col items-center space-y-2">
                <div className="p-4 rounded-full bg-brand-600/50 backdrop-blur-sm shadow-lg">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className="text-brand-50"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                </div>
                <span className="text-brand-50 text-sm font-medium px-4 py-2 rounded-full bg-brand-600/50 backdrop-blur-sm shadow-lg">
                  Click anywhere to unmute
                </span>
              </div>
            </button>
          )}

          {/* Play Button - only show when paused and not showing unmute button */}
          {!isPlaying && (!isMuted || hasInteracted) && (
            <button
              className="play-button absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hover:scale-110 transition-transform p-4 rounded-full bg-brand-800/50 z-30 shadow-lg backdrop-blur-sm"
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
              }}
              data-testid="video-player-play-button"
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="currentColor"
                stroke="none"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white"
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </button>
          )}

          {/* Video Progress Bar */}
          {(!autoPlayOnMute || hasInteracted) && (
            <div
              className={`video-controls absolute bottom-0 left-0 right-0 px-6 pt-8 pb-4 bg-gradient-to-t from-brand-800/80 via-brand-800/30 to-transparent transition-opacity duration-300 z-20 ${
                isControlsVisible ? "opacity-100" : "opacity-0"
              }`}
              onMouseEnter={handleControlsInteractionStart}
              onMouseLeave={handleControlsInteractionEnd}
              onTouchStart={handleControlsInteractionStart}
              onTouchEnd={handleControlsInteractionEnd}
            >
              <div className="flex items-center space-x-4 mb-4">
                <button
                  onClick={toggleMute}
                  className="text-white hover:text-brand-50 transition-colors focus:outline-none focus:ring-0 select-none active:outline-none p-2 rounded-full bg-brand-800/50"
                  style={{
                    WebkitTapHighlightColor: "transparent",
                    outline: "none",
                  }}
                  aria-label={isMuted ? "Unmute" : "Mute"}
                  data-testid="video-player-mute-button"
                >
                  {isMuted ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="select-none"
                    >
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="select-none"
                    >
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </svg>
                  )}
                </button>

                {hasCaptions && (
                  <button
                    onClick={toggleCaptions}
                    className={`transition-colors focus:outline-none focus:ring-0 select-none active:outline-none font-bold text-sm h-9 w-9 rounded-full flex items-center justify-center ${
                      showCaptions
                        ? "bg-brand-50 text-brand-900"
                        : "text-white bg-brand-800/50 hover:text-brand-50"
                    }`}
                    style={{
                      WebkitTapHighlightColor: "transparent",
                      outline: "none",
                    }}
                    aria-label={
                      showCaptions
                        ? "Hide Closed Captions"
                        : "Show Closed Captions"
                    }
                    data-testid="video-player-captions-button"
                  >
                    CC
                  </button>
                )}
              </div>

              <div
                className="relative cursor-pointer"
                onClick={handleProgressClick}
              >
                <div className="h-2 bg-brand-50 opacity-30 rounded"></div>
                <div
                  className="absolute top-0 left-0 h-2 bg-brand-50 opacity-50 rounded"
                  style={{ width: `${progress}%` }}
                ></div>
                <div
                  className="absolute top-0 h-2 w-2 bg-brand-50 rounded-full transform -translate-x-1/2"
                  style={{ left: `${progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-white text-xs font-bold">
                  {formatTime(currentTime)}
                </span>
                <span className="text-white text-xs font-bold">
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VideoPlayer;
